import React, { useEffect, useRef, useState } from 'react';
import { Gift, Copy, Check, Percent, Wallet, Plus } from 'lucide-react';
import { restaurantInfo } from '../data/mock';
import { track } from '../lib/track';

/**
 * Bandeau « Club Family's » — deux avantages CUMULABLES sur la première commande appli.
 *
 * Il lit /club.json au chargement (pilotable SANS redéploiement) :
 *   { "actif": true|false, "code": "FAMILY5",
 *     "titre": "🎁 Tes avantages Club Family's",
 *     "texte": "...", 
 *     "avantages": [{ "titre": "-5 %", "texte": "..." },
 *                   { "titre": "+5 %", "texte": "..." }],
 *     "exemple": "Commande de 20 € → ...", "cumul": "Les deux se cumulent",
 *     "note": "Une seule fois par compte, sur la première commande" }
 *
 * Les deux avantages sont cumulables et ne s'annulent jamais :
 *   1) -5 % de remise immédiate (code saisi dans l'appli) ;
 *   2) +5 % du montant PAYÉ, CRÉDITÉS sur la carte de fidélité (un bonus, PAS une réduction).
 * L'ordre de calcul : -5 % d'abord, puis fidélité sur ce qui est réellement payé.
 *
 * Si le fichier est absent ou illisible, on retombe sur des valeurs par
 * défaut sûres (bandeau affiché) : le site ne casse jamais.
 *
 * Mesure (compteur.php) : club_vu (affiché), club_copie (code copié),
 * club_click_ios / club_click_android (clic sur les stores).
 */
const DEFAULTS = {
  actif: true,
  code: 'FAMILY5',
  titre: "🎁 Tes avantages Club Family's",
  texte: "Deux avantages qui se cumulent sur ta première commande passée dans l'application mobile.",
  avantages: [
    { titre: '-5 %', texte: 'tout de suite, avec le code FAMILY5 saisi dans l\'appli' },
    { titre: '+5 %', texte: 'du montant payé, crédités sur ta carte de fidélité' },
  ],
  exemple: "Commande de 20 € → tu paies 19 € (les -5 %)\net tu reçois 0,95 € sur ta carte de fidélité (5 % de 19 €)\nSoit 1,95 € dans ta poche.",
  cumul: 'Les deux se cumulent',
  note: 'Une seule fois par compte, sur la première commande',
};

const AVANTAGE_ICONS = [Percent, Wallet];

// Chaîne nettoyée : non vide sinon null (jamais de valeur cassée affichée).
const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

// Avantages : ne garde que les entrées valides {titre, texte}, sinon valeurs par défaut.
const mergeAvantages = (data) => {
  if (data && Array.isArray(data.avantages)) {
    const cleaned = data.avantages
      .map((a) => (a ? { titre: clean(a.titre), texte: clean(a.texte) } : null))
      .filter((a) => a && a.titre && a.texte);
    if (cleaned.length) return cleaned;
  }
  return DEFAULTS.avantages;
};

// Fusionne les valeurs du fichier avec des garde-fous (jamais de valeur vide/cassée).
const mergeConf = (data) => ({
  actif: !data || data.actif !== false,
  code: clean(data && data.code) || DEFAULTS.code,
  titre: clean(data && data.titre) || DEFAULTS.titre,
  texte: clean(data && data.texte) || DEFAULTS.texte,
  avantages: mergeAvantages(data),
  exemple: clean(data && data.exemple) || DEFAULTS.exemple,
  cumul: clean(data && data.cumul) || DEFAULTS.cumul,
  note: clean(data && data.note) || DEFAULTS.note,
});

const ClubBanner = () => {
  // null = en cours de chargement ; ensuite config fusionnée.
  const [conf, setConf] = useState(null);
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL}/club.json?ts=${Date.now()}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((data) => { if (!cancelled) setConf(mergeConf(data)); });
    return () => { cancelled = true; };
  }, []);

  // Compte l'affichage du bandeau (une seule fois, dès que la config est connue).
  useEffect(() => {
    if (conf && conf.actif) track('club_vu');
  }, [conf]);

  // Nettoie le minuteur de « Code copié ! » au démontage.
  useEffect(() => () => { if (resetTimer.current) clearTimeout(resetTimer.current); }, []);

  if (!conf || !conf.actif) return null;

  const copyCode = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(conf.code);
      } else {
        // Secours pour contextes non sécurisés / anciens navigateurs.
        const ta = document.createElement('textarea');
        ta.value = conf.code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      track('club_copie');
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* échec de copie : on n'affiche pas de confirmation */ }
  };

  return (
    <section className="animate-on-scroll" aria-label="Club Family's">
      <div className="container mx-auto px-4 pt-6 sm:pt-8">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-yellow-400/40 bg-gradient-to-br from-red-950 via-orange-950 to-amber-950 shadow-2xl">
          <div className="px-5 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10">
            {/* Accroche */}
            <div className="mb-4 flex items-center justify-center gap-2 text-center">
              <Gift className="h-6 w-6 shrink-0 text-yellow-400" aria-hidden="true" />
              <h2 className="text-lg xs:text-xl sm:text-2xl md:text-3xl font-bold text-white leading-snug">
                {conf.titre}
              </h2>
            </div>

            <p className="mx-auto mb-5 max-w-2xl text-center text-sm sm:text-base text-gray-200 leading-relaxed">
              {conf.texte}
            </p>

            {/* Deux avantages cumulables, côte à côte */}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4">
              {conf.avantages.map((a, i) => {
                const Icon = AVANTAGE_ICONS[i] || Percent;
                return (
                  <div
                    key={a.titre}
                    className="flex flex-col items-center rounded-2xl border border-yellow-400/30 bg-black/30 p-4 text-center"
                  >
                    <Icon className="h-6 w-6 text-yellow-400" aria-hidden="true" />
                    <p className="mt-2 text-2xl font-extrabold leading-none text-yellow-300 sm:text-3xl">
                      {a.titre}
                    </p>
                    <p className="mt-2 text-xs leading-snug text-gray-200 sm:text-sm">
                      {a.texte}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Les deux se cumulent */}
            <div className="mt-4 flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-yellow-400/40 bg-yellow-400/15 px-4 py-1.5 text-sm font-bold text-yellow-200">
                <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
                {conf.cumul}
              </span>
            </div>

            {/* Exemple chiffré */}
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-center">
              <p className="whitespace-pre-line text-sm leading-relaxed text-white sm:text-base">
                {conf.exemple}
              </p>
            </div>

            {/* Code promo + bouton copier */}
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
              <div className="inline-flex items-center rounded-xl border-2 border-dashed border-yellow-400/70 bg-black/40 px-5 py-2.5">
                <span className="font-mono text-2xl sm:text-3xl font-bold tracking-[0.2em] text-yellow-300">
                  {conf.code}
                </span>
              </div>

              <button
                type="button"
                onClick={copyCode}
                aria-label={`Copier le code promo ${conf.code}`}
                className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm sm:text-base font-bold uppercase tracking-wide transition-all duration-300 shadow-lg ${
                  copied
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700'
                }`}
              >
                {copied ? <Check className="h-5 w-5" aria-hidden="true" /> : <Copy className="h-5 w-5" aria-hidden="true" />}
                {copied ? 'Code copié !' : 'Copier le code'}
              </button>

              {/* Annonce du changement d'état aux lecteurs d'écran */}
              <span aria-live="polite" className="sr-only">
                {copied ? 'Code promo copié dans le presse-papier' : ''}
              </span>
            </div>

            {/* Mention légale */}
            <p className="mt-4 text-center text-xs sm:text-sm text-white/70">{conf.note}</p>

            {/* Accès aux stores */}
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-5">
              <a
                href={restaurantInfo.appLinks.ios}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('club_click_ios')}
                aria-label="Télécharger l'application sur l'App Store"
                className="transform transition-all duration-300 hover:scale-105"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/appstore-badge.svg`}
                  alt="Télécharger sur l'App Store"
                  className="h-12 w-auto sm:h-14"
                  loading="lazy"
                />
              </a>
              <a
                href={restaurantInfo.appLinks.android}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('club_click_android')}
                aria-label="Télécharger l'application sur Google Play"
                className="transform transition-all duration-300 hover:scale-105"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/googleplay-badge.png`}
                  alt="Disponible sur Google Play"
                  className="h-12 w-auto sm:h-14"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClubBanner;
